import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { Profile } from '@selfai-platform/pipeline-common';
import { PermissionService, PipelinePermission } from '@selfai-platform/shared';
import { KeCurrentUserDomainService } from '../../current-user';

@Injectable({
  providedIn: 'root',
})
export class UserFunctionsPermissionService {
  private currentUser$ = this.keCurrentUserDomainService.getProfile();

  constructor(
    private readonly permissionService: PermissionService,
    private readonly keCurrentUserDomainService: KeCurrentUserDomainService,
  ) {
    this.keCurrentUserDomainService.loadProfileIfEmpty();
  }

  public hasCurrentPermission(permission: PipelinePermission): boolean {
    return this.getUserFunctionsPermissions().includes(permission);
  }

  public canCreateUserFunction(): boolean {
    return this.hasCurrentPermission(PipelinePermission.UserFunctionsCreate);
  }

  public canViewUserFunction(userFunctionOwnerId: string): Observable<boolean> {
    if (this.hasCurrentPermission(PipelinePermission.UserFunctionsGetAny)) {
      return of(true);
    }

    return this.equalCurrentUser(userFunctionOwnerId).pipe(
      map((equalCurrentUser) => equalCurrentUser && this.hasCurrentPermission(PipelinePermission.UserFunctionsGetOwn)),
    );
  }

  public canUpdateUserFunction(userFunctionOwnerId: string): Observable<boolean> {
    if (this.hasCurrentPermission(PipelinePermission.UserFunctionsUpdateAny)) {
      return of(true);
    }

    return this.equalCurrentUser(userFunctionOwnerId).pipe(
      map(
        (equalCurrentUser) => equalCurrentUser && this.hasCurrentPermission(PipelinePermission.UserFunctionsUpdateOwn),
      ),
    );
  }

  public canDeleteUserFunction(userFunctionOwnerId: string): Observable<boolean> {
    if (this.hasCurrentPermission(PipelinePermission.UserFunctionsDeleteAny)) {
      return of(true);
    }

    return this.equalCurrentUser(userFunctionOwnerId).pipe(
      map(
        (equalCurrentUser) => equalCurrentUser && this.hasCurrentPermission(PipelinePermission.UserFunctionsDeleteOwn),
      ),
    );
  }

  public canCloneUserFunction(userFunctionOwnerId: string): Observable<boolean> {

    if (this.hasCurrentPermission(PipelinePermission.UserFunctionsCloneAny)) {
      return of(true);
    }

    return this.equalCurrentUser(userFunctionOwnerId).pipe(
      map(
        (equalCurrentUser) => equalCurrentUser && this.hasCurrentPermission(PipelinePermission.UserFunctionsCloneOwn),
      ),
    );
  }

  public canExportUserFunction(): Observable<boolean> {
    return of(this.hasCurrentPermission(PipelinePermission.UserFunctionsExportAny) || this.hasCurrentPermission(PipelinePermission.UserFunctionsExportOwn));
  }

  public canUploadUserFunction(): Observable<boolean> {
    return of(this.hasCurrentPermission(PipelinePermission.UserFunctionsUpload));
  }

  private getUserFunctionsPermissions(): PipelinePermission[] {
    return this.permissionService.getPipelinePermissions().filter((t) => t.startsWith('functions'));
  }

  private equalCurrentUser(userId: string): Observable<boolean> {
    return (<Observable<Profile>>this.currentUser$).pipe(map((data) => data.id === userId));
  }
}
